import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ChevronRight,
  Compass,
  Waves,
  Heart,
  Zap,
  Feather,
  Moon,
  Sparkles,
  Menu,
  Check,
  X,
} from "lucide-react";
import TimeBackground from "../components/TimeBackground";
import BottomNav from "../components/BottomNav";
import ProfilePanel from "./Profile";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  getGreeting,
  THEMES,
  DIRECT_STARTERS,
  traditionById,
  TRADITIONS,
} from "../lib/constants";

const THEME_ICONS = { Compass, Waves, Heart, Zap, Feather, Moon };

function Avatar({ name, onClick }) {
  const letter = (name || "S").trim().charAt(0).toUpperCase();
  return (
    <button
      data-testid="avatar-btn"
      onClick={onClick}
      className="w-11 h-11 rounded-full flex items-center justify-center font-ui font-medium text-[#1a0d1d] shadow-lg"
      style={{
        background: "linear-gradient(135deg, #eac88a, #a07544)",
      }}
    >
      {letter}
    </button>
  );
}

function TraditionPickerModal({ open, current, onPick, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed z-[60] left-0 right-0 bottom-0 sm:left-1/2 sm:-translate-x-1/2 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:max-w-md w-full max-h-[88vh] overflow-y-auto glass rounded-t-3xl sm:rounded-3xl p-5"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4af7a] font-ui font-medium mb-1">
                  Change storyteller
                </p>
                <h2 className="font-ui font-semibold text-[20px] text-[#f8ecd4]">
                  Whose voice moves you?
                </h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#ac9470] hover:text-[#f8ecd4] hover:bg-white/5 transition-colors"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            </div>
            <div className="space-y-2.5">
              {TRADITIONS.map((t) => {
                const active = current === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => onPick(t.id)}
                    className={`w-full text-left glass rounded-2xl p-3.5 flex items-center gap-3.5 transition-all border ${
                      active
                        ? "border-[#eac88a]/70 shadow-[0_0_0_1px_rgba(234,200,138,0.4)]"
                        : "border-[#d4af7a]/15 hover:border-[#d4af7a]/40"
                    }`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl bg-cover bg-center flex-shrink-0 ring-1 ring-[#d4af7a]/25"
                      style={{ backgroundImage: `url(${t.image})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-ui font-semibold text-[#f8ecd4] text-[15.5px] leading-tight">
                          {t.name}
                        </h3>
                        {active && (
                          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#eac88a] to-[#a07544] flex items-center justify-center">
                            <Check size={10} strokeWidth={3} className="text-[#1a0d1d]" />
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] tracking-[0.18em] uppercase text-[#d4af7a] font-ui mt-0.5">
                        {t.subtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { user, updateProfile } = useAuth();
  const [verse, setVerse] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [traditionModalOpen, setTraditionModalOpen] = useState(false);
  const [savingTradition, setSavingTradition] = useState(false);
  const greeting = getGreeting();
  const trad = traditionById(user?.tradition || "all");

  useEffect(() => {
    api.get("/verse-of-the-day").then((r) => setVerse(r.data)).catch(() => {});
    api.get("/conversations").then((r) => setConversations(r.data || [])).catch(() => {});
  }, []);

  const startWith = (prompt) => {
    nav("/chat", { state: { starter: prompt } });
  };

  const handlePickTradition = async (id) => {
    if (savingTradition || id === user?.tradition) {
      setTraditionModalOpen(false);
      return;
    }
    try {
      setSavingTradition(true);
      await updateProfile({ tradition: id });
      setTraditionModalOpen(false);
    } finally {
      setSavingTradition(false);
    }
  };

  return (
    <TimeBackground overlay={0.68}>
      <div className="min-h-screen max-w-md mx-auto w-full px-5 pt-7 pb-28">
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#d4af7a] font-ui font-medium">
              {greeting.sub}
            </p>
            <h1 className="font-ui font-semibold text-[20px] sm:text-[22px] text-[#f8ecd4] leading-tight mt-1">
              {greeting.salute}, {user?.name?.split(" ")[0] || "seeker"}.
            </h1>
          </div>
          <Avatar name={user?.name} onClick={() => setPanelOpen(true)} />
        </div>

        {verse && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="glass rounded-[20px] p-4 mb-8 relative overflow-hidden verse-pulse"
            style={{
              background:
                "linear-gradient(135deg, rgba(234,200,138,0.08) 0%, rgba(20,10,30,0.6) 60%)",
            }}
          >
            <div className="flex items-center gap-2 mb-2.5">
              <Sparkles size={12} className="text-[#eac88a]" strokeWidth={1.75} />
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#d4af7a] font-ui font-medium">
                Verse of the day
              </p>
            </div>
            <p
              className="font-sacred italic text-[#f8ecd4] text-[17px] leading-[1.5] mb-2.5"
              data-testid="verse-text"
            >
              "{verse.text}"
            </p>
            <p className="text-[#ac9470] text-[13px] font-ui mb-2">{verse.context}</p>
            <div className="flex items-center justify-between pt-2 border-t border-[#d4af7a]/15">
              <p className="text-[10.5px] tracking-[0.2em] uppercase text-[#d4af7a] font-ui font-medium">
                {verse.source}
              </p>
              <button
                data-testid="verse-explore-btn"
                onClick={() => startWith(`Help me sit with this verse: "${verse.text}" — ${verse.source}`)}
                className="text-[13px] text-[#f0d09c] hover:text-[#f8ecd4] font-ui flex items-center gap-1 transition-colors"
              >
                Explore <ArrowRight size={13} strokeWidth={1.75} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Primary CTA — avatar taps to swap tradition, card body taps to chat */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="cta-pulse w-full glass rounded-[22px] p-5 mb-8 flex items-center gap-4 hover:border-[#d4af7a]/60 transition-all"
        >
          <button
            data-testid="swap-tradition-btn"
            onClick={() => setTraditionModalOpen(true)}
            aria-label="Change storyteller"
            className="relative flex-shrink-0 group"
          >
            <div
              className="w-[62px] h-[62px] rounded-xl bg-cover bg-center ring-1 ring-[#eac88a]/45 group-hover:ring-[#eac88a] transition"
              style={{ backgroundImage: `url(${trad.image})` }}
            />
            <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#1a0f2e] border border-[#d4af7a]/50 flex items-center justify-center text-[10px] text-[#d4af7a] font-ui font-medium">
              ⇄
            </span>
          </button>
          <button
            data-testid="talk-to-tradition-btn"
            onClick={() => nav("/chat")}
            className="flex-1 min-w-0 text-left flex items-center gap-2"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4af7a] font-ui font-medium">
                Today
              </p>
              <h3 className="font-ui font-semibold text-[17px] text-[#f8ecd4] leading-tight mt-0.5">
                Talk to {trad.name}
              </h3>
              <p className="text-[#ac9470] text-[13px] font-ui mt-1 leading-[1.5]">
                Ask anything. Voice or text.
              </p>
            </div>
            <ChevronRight size={20} className="text-[#d4af7a]" strokeWidth={1.75} />
          </button>
        </motion.div>

        <div className="mb-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-ui font-semibold text-[17px] text-[#f8ecd4]">Explore by theme</h2>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#ac9470] font-ui font-medium">
              Tap to begin
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {THEMES.map((t) => {
              const Icon = THEME_ICONS[t.icon];
              return (
                <button
                  key={t.id}
                  data-testid={`theme-${t.id}`}
                  onClick={() => startWith(t.prompt)}
                  className="theme-card glass rounded-2xl p-4 text-left hover:border-[#d4af7a]/45"
                >
                  <div className="theme-icon w-9 h-9 rounded-lg bg-gradient-to-br from-[#eac88a]/20 to-[#a07544]/10 border border-[#d4af7a]/25 flex items-center justify-center mb-3">
                    <Icon size={16} strokeWidth={1.5} className="text-[#eac88a]" />
                  </div>
                  <h3 className="font-ui font-medium text-[#f8ecd4] text-[15px] leading-tight">
                    {t.title}
                  </h3>
                  <p className="text-[#ac9470] text-[12px] font-ui mt-1">{t.subtitle}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="font-ui font-semibold text-[17px] text-[#f8ecd4] mb-4">
            Or ask something direct
          </h2>
          <div className="space-y-2">
            {DIRECT_STARTERS.map((s, i) => (
              <button
                key={i}
                data-testid={`starter-${i}`}
                onClick={() => startWith(s)}
                className="w-full glass rounded-2xl px-4 py-3.5 flex items-center justify-between text-left transition-all hover:border-[#d4af7a]/45"
              >
                <span className="font-ui text-[#f8ecd4]/90 text-[14px] leading-[1.5]">{s}</span>
                <ChevronRight size={16} className="text-[#d4af7a] flex-shrink-0 ml-2" strokeWidth={1.75} />
              </button>
            ))}
          </div>
        </div>

        {conversations.length > 0 && (
          <div className="mb-6">
            <h2 className="font-ui font-semibold text-[17px] text-[#f8ecd4] mb-4">
              Continue where you left off
            </h2>
            <div className="space-y-2">
              {conversations.slice(0, 3).map((c) => (
                <button
                  key={c.id}
                  data-testid={`recent-${c.id}`}
                  onClick={() => nav(`/chat/${c.id}`)}
                  className="w-full glass rounded-2xl px-4 py-3 text-left flex items-center justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-ui text-[#f8ecd4] text-[14px] truncate">
                      {c.title}
                    </p>
                    <p className="text-[11px] text-[#ac9470] font-ui mt-0.5">
                      {new Date(c.updated_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      · {c.message_count} {c.message_count === 1 ? "turn" : "turns"}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-[#d4af7a] ml-2" strokeWidth={1.75} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <BottomNav />
      <ProfilePanel open={panelOpen} onClose={() => setPanelOpen(false)} />
      <TraditionPickerModal
        open={traditionModalOpen}
        current={user?.tradition}
        onPick={handlePickTradition}
        onClose={() => setTraditionModalOpen(false)}
      />
    </TimeBackground>
  );
}